import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, ChevronRight, X, Package2 } from 'lucide-react';
import RequestForm from '../services/RequestForm';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isAuthenticated: boolean;
  onLogout: () => void;
}

function MobileMenu({ isOpen, onClose, isAuthenticated }: MobileMenuProps) {
  const navigate = useNavigate();
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [showRequestForm, setShowRequestForm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmenuClick = (menu: string) => {
    setActiveSubmenu(activeSubmenu === menu ? null : menu);
  };

  const renderSubmenu = (menu: string) => {
    switch (menu) {
      case 'services':
        return (
          <div className="bg-[#1B3BA0]/5">
            <div>
              <a href="/services/business/search" className="block px-6 py-3 text-[17px] text-gray-700 hover:text-[#1B3BA0] hover:bg-[#1B3BA0]/10 transition-all">
                Поиск и выкуп товаров
              </a>
              <a href="/services/business/logistics" className="block px-6 py-3 text-[17px] text-gray-700 hover:text-[#1B3BA0] hover:bg-[#1B3BA0]/10 transition-all">
                Логистика и таможня
              </a>
              <a href="/services/business/forwarding" className="block px-6 py-3 text-[17px] text-gray-700 hover:text-[#1B3BA0] hover:bg-[#1B3BA0]/10 transition-all">
                Инспекционный контроль
              </a>
              <a href="/services/business/payment" className="block px-6 py-3 text-[17px] text-gray-700 hover:text-[#1B3BA0] hover:bg-[#1B3BA0]/10 transition-all">
                Оплата поставщикам
              </a>
            </div>
          </div>
        );
      case 'prices':
        return (
          <div className="bg-[#1B3BA0]/5">
            <a href="/prices/business" className="block px-6 py-3 text-[17px] text-gray-700 hover:text-[#1B3BA0] hover:bg-[#1B3BA0]/10 transition-all">
              Для бизнеса
            </a>
            <a href="/prices/personal" className="block px-6 py-3 text-[17px] text-gray-700 hover:text-[#1B3BA0] hover:bg-[#1B3BA0]/10 transition-all">
              Для частных лиц
            </a>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-50 transition-all duration-300 ${
        isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
      }`}
    >
      <div 
        className={`fixed inset-0 bg-white transform transition-transform duration-500 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="h-12 flex items-center justify-between px-4 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between w-full">
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-primary transition-all rounded-full hover:bg-gray-100 active:scale-95"
            >
              <X className="h-5 w-5" />
            </button>
            
            <img 
              src="https://i.ibb.co/sJQNRg3S/image.png"
              alt="997 Cargo"
              className="h-[54px]"
            />
            
            {isAuthenticated ? (
              <button
                onClick={() => {
                  navigate('/client/dashboard');
                  onClose();
                }}
                className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-[#1B3BA0] transition-colors rounded-full hover:bg-gray-100 active:scale-95"
              >
                <User className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={() => {
                  navigate('/login'); 
                  onClose();
                }}
                className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-[#1B3BA0] transition-colors rounded-full hover:bg-gray-100 active:scale-95"
              >
                <User className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Menu Content */}
        <div className="h-[calc(100%-3rem)] overflow-auto">
          <div className="py-2">
            <button
              onClick={() => handleSubmenuClick('services')}
              className="flex items-center justify-between w-full px-6 py-4 text-[17px] text-gray-700 font-medium hover:bg-[#1B3BA0]/5 transition-all rounded-xl mx-2"
            >
              <span>Услуги</span>
              <ChevronRight className={`w-5 h-5 transition-transform duration-300 ${
                activeSubmenu === 'services' ? 'rotate-90' : ''
              }`} />
            </button>
            {activeSubmenu === 'services' && renderSubmenu('services')}
            
            <a 
              href="/prices"
              className="block px-6 py-4 text-[17px] text-gray-700 font-medium hover:bg-[#1B3BA0]/5 transition-all rounded-xl mx-2"
              onClick={onClose}
            >
              Цены
            </a>

            <button
              onClick={() => setShowRequestForm(true)}
              className="flex items-center w-full px-6 py-4 text-[17px] text-gray-700 font-medium hover:bg-[#1B3BA0]/5 transition-all rounded-xl mx-2 my-2"
            >
              <Package2 className="w-5 h-5 mr-2" />
              Заказать доставку
            </button>

            <a 
              href="/blog"
              className="block px-6 py-4 text-[17px] text-gray-700 font-medium hover:bg-[#1B3BA0]/5 transition-all rounded-xl mx-2"
              onClick={onClose}
            >
              Блог
            </a>
            <a 
              href="/about"
              className="block px-6 py-4 text-[17px] text-gray-700 font-medium hover:bg-[#1B3BA0]/5 transition-all rounded-xl mx-2"
              onClick={onClose}
            >
              О нас
            </a>
            <a 
              href="/contacts"
              className="block px-6 py-4 text-[17px] text-gray-700 font-medium hover:bg-[#1B3BA0]/5 transition-all rounded-xl mx-2"
              onClick={onClose}
            >
              Контакты
            </a>
          </div>
        </div>
      </div>

      {/* Request Form Popup */}
      {showRequestForm && (
        <RequestForm 
          isPopup 
          source="Мобильное меню"
          onClose={() => {
            setShowRequestForm(false);
            // Don't close the mobile menu when closing the form
          }}
        />
      )}
    </div>
  );
}

export default MobileMenu;